import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtTime } from "@/lib/api";
import { Panel, Pill, Stat } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Wrench, Stethoscope } from "lucide-react";

const TONE = { ok: "green", warn: "amber", fail: "red", crit: "red", info: "slate" };

function Finding({ f, i }) {
  return (
    <div data-testid={`diagnosis-finding-${i}`} className="grid grid-cols-[56px_1fr] gap-2 py-2 border-b border-slate-800/60 text-xs">
      <Pill tone={TONE[f.level]}>{f.level === "crit" ? "problem" : f.level}</Pill>
      <div>
        <div className="text-slate-100 font-semibold">{f.title}</div>
        <div className="text-slate-400">{f.explain}</div>
        {f.fix !== "—" && <div className="text-cyan-300 mt-0.5">Fix: {f.fix}</div>}
      </div>
    </div>
  );
}

function Diagnosis({ d }) {
  return (
    <div data-testid="diagnosis-results" className="mb-4 rounded border border-slate-700 bg-[#0B111B] px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Diagnosis</div>
      {d.findings.map((f, i) => <Finding key={i} f={f} i={i} />)}
    </div>
  );
}

function CheckRow({ c }) {
  return (
    <div data-testid={`check-row-${c.id}`} className="grid grid-cols-[56px_1fr] gap-2 py-1.5 border-b border-slate-800/60 text-xs">
      <Pill tone={TONE[c.status]}>{c.status}</Pill>
      <div>
        <div className="text-slate-200 flex items-center gap-2">{c.label}{c.auto_fixed && <span className="text-[10px] text-cyan-400 flex items-center gap-1"><Wrench size={11} /> auto-corrected</span>}</div>
        <div className="font-mono text-[11px] text-slate-500">{c.detail}</div>
      </div>
    </div>
  );
}

export function OutputQuality({ cid, r, running }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [dbusy, setDbusy] = useState(false);
  const diagnose = async () => {
    setDbusy(true);
    try { const { data } = await api.post(`/admin/channels/${cid}/diagnose`); setDiag(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setDbusy(false); }
  };
  useEffect(() => { api.get(`/admin/channels/${cid}/check`).then(({ data }) => data?.checks && setRes(data)).catch(() => {}); }, [cid]);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/channels/${cid}/check`); setRes(data); toast.success(`Stream check: ${data.status}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const sm = r.smoothing || {};
  const fx = r.corrections || {};
  return (
    <Panel title="Output quality" testid="output-quality-panel"
      right={<div className="flex gap-2"><Button data-testid="diagnose-btn" size="sm" variant="outline" disabled={!running || dbusy} onClick={diagnose} className="border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10"><Stethoscope size={13} /> {dbusy ? "Diagnosing…" : "Diagnose"}</Button><Button data-testid="run-stream-check-btn" size="sm" disabled={!running || busy} onClick={run} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"><ShieldCheck size={13} /> {busy ? "Checking…" : "Run stream check"}</Button></div>}>
      {diag && <Diagnosis d={diag} />}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
        <Stat label="Smoothing buffer" value={sm.target ? `auto ${sm.target}s` : "off (steady)"} testid="stat-smoothing-target" />
        <Stat label="Held now" value={sm.buffered != null ? `${sm.buffered}s` : "—"} testid="stat-smoothing-held" />
        <Stat label="Buffer underruns" value={sm.underruns ?? "—"} testid="stat-smoothing-underruns" />
        <Stat label="CC repairs" value={fx.cc_fixed ?? "—"} testid="stat-cc-fixed" />
        <Stat label="Timestamp re-times" value={fx.ts_rebased ?? "—"} testid="stat-ts-rebased" />
        <Stat label="Keyframes marked" value={fx.keyframes_marked ?? "—"} testid="stat-keyframes-marked" />
      </div>
      {!res && <p className="text-xs text-slate-500">The check reads the output the way a restreaming server (nginx-rtmp / SRS / ffmpeg) does. It runs automatically every 5 minutes; problems that cannot be auto-corrected raise an incident and alert.</p>}
      {res && (
        <div data-testid="stream-check-results">
          <div className="flex items-center gap-2 mb-1 text-xs text-slate-400">
            Overall <Pill tone={TONE[res.status]} testid="stream-check-status">{res.status}</Pill>
            <span className="font-mono">{fmtTime(res.ran_at, true)} · {res.segments_checked} segments · {res.took_ms} ms</span>
          </div>
          {res.checks.map((c) => <CheckRow key={c.id} c={c} />)}
        </div>
      )}
    </Panel>
  );
}
